import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Button, Input, SelectField } from "components";
import { useGetAllCustomerSites } from "controller";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import type { AddCustomerUserFormValues, CustomerSiteResultType } from "shared";
import { isEmailValid } from "utilz/helpers";
import * as yup from "yup";
import { validateEmailLocalPart } from "../../../customers/components/on-boarding-customer/onBoardingDrawerComponents/helpers";
import {
  CategorySelectField,
  useCustomerUserCategoryOptions,
} from "./CategorySelectField";
import { PERMISSION_OPTIONS, PERMISSION_TOOLTIP } from "./constants";
import {
  DUPLICATE_EMAIL_MESSAGE,
  isDuplicatePendingEmail,
  makePendingId,
} from "./helpers";
import {
  addPendingCustomerUserAtom,
  pendingCustomerUsersAtom,
} from "../../store/addCustomerUsersAtoms";

export const addUserSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required("Name is required")
    .max(60, "Name cannot exceed 60 characters")
    .matches(/\p{L}/u, {
      message: "Name must contain at least one letter",
      excludeEmptyString: true,
    }),
  email: yup
    .string()
    .trim()
    .required("Email is required")
    .max(75, "Email cannot exceed 75 characters")
    .test("format", "Invalid email format", (value) =>
      value ? isEmailValid(value) : false,
    )
    // Mirrors the backend's local-part rules, reusing the existing helper so the
    // two manual paths agree on what a legal address looks like.
    .test("local-part", (value, ctx) => {
      if (!value) return true;
      const result = validateEmailLocalPart(value.split("@")[0]);
      return (
        result.isValid ||
        ctx.createError({ message: result.error || "Invalid email username" })
      );
    }),
  site: yup.string(),
  permission: yup.string(),
  category: yup.string(),
});

export const BasicInformationForm = ({
  customerId,
  onDirtyChange,
  isSubmitting = false,
}: {
  customerId: string;
  onDirtyChange: (isDirty: boolean) => void;
  /** A row queued mid-submit is not part of the in-flight batch and would be
   *  discarded by the drawer's reset, so the button is closed while sending. */
  isSubmitting?: boolean;
}) => {
  const theme = useTheme();
  const pendingUsers = useAtomValue(pendingCustomerUsersAtom);
  const addPendingUser = useSetAtom(addPendingCustomerUserAtom);

  const { data: sitesData } = useGetAllCustomerSites({
    customerId,
    sortColumn: "site_name",
    sortDirection: "asc",
  });

  const siteOptions = useMemo(
    () =>
      Array.isArray(sitesData?.result)
        ? sitesData.result.filter(
            (site: CustomerSiteResultType) => site?.is_active,
          )
        : [],
    [sitesData?.result],
  );

  const defaultSiteValue = useMemo(
    () =>
      siteOptions.find(
        (site: CustomerSiteResultType) => site?.meta?.is_default === true,
      )?.mapping_id ?? "",
    [siteOptions],
  );

  const { options: categoryOptions, defaultValue: defaultCategoryValue } =
    useCustomerUserCategoryOptions();

  const formDefaults = useMemo<AddCustomerUserFormValues>(
    () => ({
      name: "",
      email: "",
      site: defaultSiteValue,
      permission: "standard",
      category: defaultCategoryValue,
    }),
    [defaultSiteValue, defaultCategoryValue],
  );

  const {
    control,
    reset,
    trigger,
    setError,
    getValues,
    watch,
    formState: { errors },
  } = useForm<AddCustomerUserFormValues>({
    defaultValues: {
      name: "",
      email: "",
      site: "",
      permission: "standard",
      category: "",
    },
    resolver: yupResolver(addUserSchema),
  });

  // Prefill Site + Category once their options resolve, but never clobber a
  // pick the user already made.
  useEffect(() => {
    if (defaultSiteValue && !getValues("site")) {
      reset({ ...getValues(), site: defaultSiteValue });
    }
  }, [defaultSiteValue, getValues, reset]);

  // Category prefill has to survive a swap of the option list, not just an empty
  // field. On first render the API hasn't answered yet, so the options are the
  // offline FALLBACK_CATEGORIES whose ids are placeholders ("standard"), and the
  // field gets seeded with one. When the real rows arrive their ids are UUIDs,
  // so the seeded placeholder matches no option and the SelectField renders
  // BLANK. Re-sync whenever the held value is absent from the current options.
  useEffect(() => {
    if (!defaultCategoryValue) return;
    const current = getValues("category");
    const isCurrentStillValid =
      Boolean(current) &&
      categoryOptions.some((option) => option.mapping_id === current);
    if (!isCurrentStillValid) {
      reset({ ...getValues(), category: defaultCategoryValue });
    }
  }, [defaultCategoryValue, categoryOptions, getValues, reset]);

  // The drawer's close guard asks "is there work in progress?" — only typed
  // name/email counts, since site/permission/category are auto-defaults.
  const watchedName = watch("name");
  const watchedEmail = watch("email");
  useEffect(() => {
    onDirtyChange(
      Boolean(watchedName?.trim()) || Boolean(watchedEmail?.trim()),
    );
  }, [watchedName, watchedEmail, onDirtyChange]);

  // Validate via trigger(), NOT handleSubmit. handleSubmit flips isSubmitted,
  // which makes RHF re-validate the next fresh row on every keystroke — a
  // half-typed email would read "invalid" before the user clicks Add again.
  const onAddToList = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    const values = getValues();
    const email = values.email.trim();

    if (isDuplicatePendingEmail(email, pendingUsers)) {
      setError("email", {
        type: "duplicate",
        message: DUPLICATE_EMAIL_MESSAGE,
      });
      return;
    }

    const site = siteOptions.find(
      (option: CustomerSiteResultType) => option.mapping_id === values.site,
    );
    const category = categoryOptions.find(
      (option) => option.mapping_id === values.category,
    );

    addPendingUser({
      id: makePendingId(),
      name: values.name.trim(),
      email,
      siteValue: values.site ?? "",
      siteLabel: site?.site_name ?? null,
      isPrivileged: values.permission === "privileged",
      categoryValue: values.category ?? "",
      categoryLabel: category?.name ?? null,
      categoryTier: category?.tier ?? null,
      failedReason: null,
    });

    reset(formDefaults);
  };

  return (
    <Stack
      sx={{
        flex: 1,
        justifyContent: "space-between",
        gap: "16px",
        minWidth: 0,
      }}
    >
      <Stack gap="16px">
        <Stack gap="4px">
          <Typography
            variant="body-medium"
            sx={{ fontWeight: 600, color: theme.palette.lightBg.high }}
          >
            Basic information
          </Typography>
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.lightBg.low }}
          >
            Add user's primary account details
          </Typography>
        </Stack>

        <Input
          control={control}
          name="name"
          label="Name"
          required
          placeholder="Enter name"
          error={!!errors.name}
          errorMessage={errors.name?.message}
        />
        <Input
          control={control}
          name="email"
          label="Email"
          required
          placeholder="Enter email address"
          error={!!errors.email}
          errorMessage={errors.email?.message}
        />
        <SelectField
          control={control}
          name="site"
          label="Site"
          placeholder="Select site"
          options={siteOptions}
          valueMappingKey="mapping_id"
          labelMappingKey="site_name"
          isSearchable
          error={!!errors.site}
          errorMessage={errors.site?.message}
        />
        <SelectField
          control={control}
          name="permission"
          label="Permission"
          tooltip={PERMISSION_TOOLTIP}
          placeholder="Select permission"
          options={PERMISSION_OPTIONS}
          valueMappingKey="value"
          labelMappingKey="label"
          error={!!errors.permission}
          errorMessage={errors.permission?.message}
        />
        <CategorySelectField control={control} />
      </Stack>

      <Box sx={{ width: "100%" }}>
        <Button
          variant="secondary"
          onClick={onAddToList}
          disabled={isSubmitting}
          sx={{ width: "100%" }}
        >
          Add to list
        </Button>
      </Box>
    </Stack>
  );
};
